import { Box } from "@mui/material";
import {
  ComponentProps,
  forwardRef,
  MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";

import { Workspace } from "@/components/graphql/schema.graphql";
import { getModifier } from "@/utils/selectInList";

import LoadingSpinnerPage from "../../common/LoadingSpinnerPage";
import { EmptyResult } from "../../components/list/EmptyResult";
import { useGridShortcuts } from "../../hooks/useGridShortcuts";
import { useDrive } from "./DriveContext";
import DriveItemsBody from "./DriveItemsBody";
import DriveItemsHeader from "./DriveItemsHeader";
import { DriveFileFragment } from "./queries.graphql";
import { DriveBodyContextMenu } from "./tools/DriveBodyContextMenu";
import { DriveEmptyState } from "./tools/DriveEmptyState";
import { DriveItemContextMenu } from "./tools/DriveItemContextMenu";
import { DownloadOptions, DriveItem } from "./types";
import { createGrid } from "./utils";
type DriveItemHandle = {
  resetCache: () => void;
};

type DriveItemsProps = {
  workspace: Pick<Workspace, "id" | "name">;
  objects: DriveItem[];
  refreshView: () => void;
  onActivateItem: (item: DriveItem) => void;
  onDownloadFiles: (
    files: DriveFileFragment[],
    options: DownloadOptions,
  ) => void;
  fetching?: boolean;
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  loadNextPage: ComponentProps<typeof DriveItemsBody>["loadNextPage"];
  columnCount: number;
  isFiltered: boolean;
  onClearFilters: () => void;
};

const DriveItems = forwardRef<DriveItemHandle, DriveItemsProps>(
  (props, ref) => {
    const {
      workspace,
      objects,
      refreshView,
      onActivateItem,
      onDownloadFiles,
      fetching = false,
      hasNextPage,
      isNextPageLoading,
      loadNextPage,
      columnCount,
      isFiltered,
      onClearFilters,
    } = props;
    const { actionsDisabled } = useDrive();
    const [itemContextMenuAnchor, setItemContextMenuAnchor] = useState<
      { position: { left: number; top: number } } | undefined
    >(undefined);
    const [bodyContextMenuAnchor, setBodyContextMenuAnchor] = useState<
      { position: { left: number; top: number } } | undefined
    >(undefined);

    const {
      viewType,
      selectedItems,
      setSelectedItems,
      selectAllItems,
      selectableItemTypes,
      isPreviewOpen,
      setIsPreviewOpen,
      previewIndex,
      setPreviewIndex,
    } = useDrive();

    const onClickItem = useCallback(
      (event: MouseEvent, item: DriveItem) => {
        event.stopPropagation();
        event.preventDefault();

        const modifier = getModifier(event);
        setSelectedItems(item.id, objects, modifier);
      },
      [objects, setSelectedItems, selectableItemTypes],
    );

    const onDoubleClickItem = useCallback(
      (event: MouseEvent, item: DriveItem) => {
        event.stopPropagation();
        event.preventDefault();
        onActivateItem(item);
      },
      [onActivateItem],
    );

    const onContextMenuItem = useCallback(
      (event: MouseEvent, item: DriveItem) => {
        event.preventDefault();
        event.stopPropagation();
        const isAlreadySelected = selectedItems?.some((i) => i.id === item.id);

        if (!isAlreadySelected) {
          setSelectedItems(item.id, objects);
        }

        setBodyContextMenuAnchor(undefined);
        setItemContextMenuAnchor({
          position: { left: event.clientX, top: event.clientY },
        });
      },
      [
        objects,
        setItemContextMenuAnchor,
        setBodyContextMenuAnchor,
        setSelectedItems,
        selectedItems,
      ],
    );

    const onContextMenuBody = useCallback(
      (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedItems(null, []);
        setItemContextMenuAnchor(undefined);
        setBodyContextMenuAnchor({
          position: { left: event.clientX, top: event.clientY },
        });
      },
      [setItemContextMenuAnchor, setBodyContextMenuAnchor],
    );

    const handleContextMenuClose = useCallback(() => {
      setItemContextMenuAnchor(undefined);
      setBodyContextMenuAnchor(undefined);
    }, [setItemContextMenuAnchor, setBodyContextMenuAnchor]);

    const grid = useMemo(
      () =>
        createGrid(
          objects.map((item) => item.id),
          columnCount,
        ),
      [objects, columnCount],
    );

    const selectedSet = useMemo(() => {
      const selectedIds = new Set<string>(
        selectedItems?.map((item) => item.id),
      );
      return selectedIds;
    }, [selectedItems]);

    // Handling keyboard shortcuts
    useGridShortcuts({
      grid,
      selectedItems: selectedSet,
      focusedObjectId:
        previewIndex == null ? null : selectedItems[previewIndex]?.id,
      previewOpen: isPreviewOpen,
      onSelectAll: () => selectAllItems(objects),
      onExitContext: () => {
        if (isPreviewOpen) {
          setIsPreviewOpen(false);
        } else {
          setSelectedItems(null, []);
        }
      },
      onPreviewToggle: (next) => {
        setIsPreviewOpen(next);
      },
      onActivate: () => {
        if (selectedItems.length === 1) {
          onActivateItem(selectedItems[0]);
        }
      },
      onPreviewItemChanged: (direction) => {
        if (selectedItems.length === 0 || previewIndex === null) {
          return;
        }
        let nextIndex =
          direction === "next"
            ? (previewIndex + 1) % selectedItems.length
            : previewIndex - 1;

        if (nextIndex < 0) {
          nextIndex = selectedItems.length - 1;
        }

        setPreviewIndex(nextIndex);
      },
      onSelectionChanged: (objectId, modifier) =>
        setSelectedItems(objectId, objects, modifier),
    });

    if (!objects.length && fetching) {
      return (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <LoadingSpinnerPage />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
        onContextMenu={onContextMenuBody}
      >
        {!fetching && objects?.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
            }}
          >
            {isFiltered && (
              <EmptyResult
                noItemsText="No drive item matches your selected filters"
                onClearFilters={onClearFilters}
              />
            )}
            {!isFiltered && <DriveEmptyState />}
          </Box>
        ) : (
          <>
            <DriveItemsHeader viewType={viewType} />
            <DriveItemsBody
              ref={ref}
              items={objects}
              onClickItem={onClickItem}
              onDoubleClickItem={onDoubleClickItem}
              onContextMenuItem={onContextMenuItem}
              hasNextPage={hasNextPage}
              isNextPageLoading={isNextPageLoading}
              loadNextPage={loadNextPage}
              columnCount={columnCount}
            />
          </>
        )}
        {!actionsDisabled && (
          <DriveBodyContextMenu
            anchor={bodyContextMenuAnchor}
            workspace={workspace}
            onClose={handleContextMenuClose}
          />
        )}
        {!actionsDisabled && (
          <DriveItemContextMenu
            anchor={itemContextMenuAnchor}
            onDownloadFiles={onDownloadFiles}
            onClose={handleContextMenuClose}
            refreshView={refreshView}
          />
        )}
      </Box>
    );
  },
);

export default DriveItems;
